import { triggerResource, upload } from '@/api';

/**
 * ISSUE-021 — shared "presigned PUT -> POST /resources/{id}/trigger" sequence.
 *
 * Every upload call site must go through this helper so the trigger call can
 * never drift from the PUT (FR-081). The trigger tells the backend the upload
 * finished so transcoding starts without an object-storage event pipeline
 * (RESOURCE_USE_SQS=false).
 */
export interface UploadItem {
  resourceId: string;
  uploadUrl: string;
  file: File;
}

// Client-side idempotency guard (AC-5): once a resource has been triggered
// successfully it is never triggered again by this client, so an accidental
// re-invocation for the same resource cannot enqueue a duplicate transcode
// job. A failed attempt is NOT recorded, so an explicit retry may re-trigger.
const triggeredResourceIds = new Set<string>();

/** Test hook — clears the module-level idempotency guard. */
export const resetTriggeredResources = () => {
  triggeredResourceIds.clear();
};

/**
 * PUT the file to its presigned URL, then trigger transcoding exactly once.
 * Never throws: a failure at either step reports the resource id through
 * `onFailed` (callers mark it FAILED in their uploading-state atom) and
 * resolves `false`, so a batch caller can continue with the remaining files.
 */
export const uploadAndTrigger = async (
  { resourceId, uploadUrl, file }: UploadItem,
  onFailed?: (resourceId: string) => void
): Promise<boolean> => {
  try {
    await upload(uploadUrl, file);
  } catch (error) {
    onFailed?.(resourceId);
    return false;
  }
  if (!triggeredResourceIds.has(resourceId)) {
    try {
      await triggerResource(resourceId);
      triggeredResourceIds.add(resourceId);
    } catch (error) {
      onFailed?.(resourceId);
      return false;
    }
  }
  return true;
};

/**
 * Sequential batch upload: each resource is triggered immediately after its
 * own PUT resolves (not batched at the end), and a mid-batch failure marks
 * only that file FAILED while the rest of the batch continues.
 */
export const uploadAllAndTrigger = async (
  items: UploadItem[],
  onFailed?: (resourceId: string) => void
): Promise<boolean> => {
  let allSucceeded = true;
  for (let i = 0; i < items.length; i++) {
    const succeeded = await uploadAndTrigger(items[i], onFailed);
    allSucceeded = allSucceeded && succeeded;
  }
  return allSucceeded;
};
